import { withScope } from "@sentry/react";
import CustomError from "lib/core/customError";
import { CheckoutErrorType, ErrorType } from "lib/types/error";
import { C2P_ERROR_MESSAGES } from "lib/utils/constants";

/**
 * Triggers Sentry error logging for checkout errors with specific conditions
 * Excludes 403 errors and C2P error messages from being logged to Sentry
 *
 * @param error - CustomError with CheckoutErrorType extraData
 * @param errorType - Type of error to tag in Sentry (defaults to CHECKOUT_ERROR)
 * @returns boolean - true if error was logged to Sentry, false if excluded
 */
export const triggerSentryError = (error: CustomError<CheckoutErrorType>, errorType: ErrorType): boolean => {
  const { err, url, payload } = error.extraData;

  // Skip 403 errors
  if (err?.response?.status === 403) {
    return false;
  }

  // Skip C2P error messages
  if (err?.response?.status === 400 && C2P_ERROR_MESSAGES.includes(err?.response?.data?.error)) {
    return false;
  }

  // Log to Sentry if error doesn't match exclusion conditions
  withScope((scope) => {
    scope.setTag("error_type", errorType);
    scope.setExtra("url", url);
    scope.setExtra("payload", payload);
    scope.setExtra("errStatus", err?.response?.status);
    scope.setExtra("errMessage", err?.response?.data?.error);
    scope.captureException(err);
  });

  return true;
};

export const extractApiError = (e: unknown): { errorObj: unknown; errorMsg: string } => {
  const axiosError = e as { response?: { data?: { error?: unknown } } };
  const errorObj = axiosError?.response?.data?.error;
  const msg =
    typeof errorObj === "object" && errorObj !== null
      ? (errorObj as Record<string, unknown>).message
      : errorObj;
  return { errorObj, errorMsg: typeof msg === "string" ? msg : "" };
};

export const triggerFuncSentryError = ({
  errorType,
  extraData,
  errMessage,
}: {
  errorType: ErrorType;
  extraData: any;
  errMessage: string;
}) => {
  withScope((scope) => {
    scope.setTag("error_type", errorType);
    scope.setExtra("payload", extraData);
    scope.captureException(new Error(errMessage));
  });
};
